import React, { Fragment, useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import BookingPublicLayout from "../bookingPublic/BookingPublicLayout";
import {
  CONSTANTS,
  COLORS,
  FONTWEIGHT,
  FONTSIZE,
  BREAKPOINTS
} from "helpers/designConstants";
import { useSearchParams } from "react-router-dom";
import { useCatalogueDatabase } from "database/useCatalogueDatabase";
import { useArticleReader } from "database/articles/useArticleReader";
import { cloneDeep } from "lodash";
import ArticleZoomModal from "components/shared/articleZoomModal/ArticleZoomModal";
import InventoryModal from "components/shared/inventoryModal/InventoryModal";
import useArticleZoomModal from "components/shared/articleZoomModal/useArticleZoomModal";
import useInventoryModal from "components/shared/inventoryModal/useInventoryModal";
import ArticleCard from "components/shared/ArticleCard";
import Loading from "components/shared/Loading";
import { GroupedVirtuoso } from "react-virtuoso";
import CatalogueViewerTOC from "./CatalogueViewerTOC";

// Sort articles based on per-group sortMode
const sortViewerArticles = (articles, sortMode, articleIDs) => {
  if (!articles || articles.length === 0) return articles;
  switch (sortMode) {
    case "name_asc":
      return [...articles].sort((a, b) =>
        (a.articleName || "").localeCompare(b.articleName || "")
      );
    case "price_desc":
      return [...articles].sort(
        (a, b) => (b.retailPrice || 0) - (a.retailPrice || 0)
      );
    case "price_asc":
      return [...articles].sort(
        (a, b) => (a.retailPrice || 0) - (b.retailPrice || 0)
      );
    case "custom": {
      if (!articleIDs) return articles;
      const idxMap = new Map(articleIDs.map((id, i) => [id, i]));
      return [...articles].sort(
        (a, b) => (idxMap.get(a.id) ?? Infinity) - (idxMap.get(b.id) ?? Infinity)
      );
    }
    case "default":
    default:
      return [...articles].sort(
        (a, b) => (b.retailPrice || 0) - (a.retailPrice || 0)
      );
  }
};

// Recursively flatten navTree + articlesByKey into flat arrays for Virtuoso and TOC
const flattenNavTree = (navTree, articlesByKey) => {
  const flatGroups = [];
  const tocEntries = [];

  const walk = (nodes, depth, breadcrumb, breadcrumb_fr) => {
    let addedLeaves = 0;
    nodes.forEach((node) => {
      const name = node.name || "";
      const name_fr = node.name_fr || name;

      if (node.groups && node.groups.length > 0) {
        // Parent node — reserve slot, remove if no non-empty descendants
        const insertIndex = tocEntries.length;
        tocEntries.push({
          key: node.key,
          name,
          name_fr,
          depth,
          isLeaf: false,
          flatGroupIndex: null
        });
        const childLeaves = walk(
          node.groups,
          depth + 1,
          [...breadcrumb, name],
          [...breadcrumb_fr, name_fr]
        );
        if (childLeaves === 0) {
          tocEntries.splice(insertIndex, 1);
        } else {
          addedLeaves += childLeaves;
        }
      } else {
        // Leaf node
        const articles = articlesByKey[node.key] || [];
        if (articles.length === 0) return; // skip empty

        const groupIndex = flatGroups.length;
        flatGroups.push({
          key: node.key,
          name,
          name_fr,
          breadcrumb: [...breadcrumb, name],
          breadcrumb_fr: [...breadcrumb_fr, name_fr],
          articles
        });
        tocEntries.push({
          key: node.key,
          name,
          name_fr,
          depth,
          isLeaf: true,
          flatGroupIndex: groupIndex
        });
        addedLeaves++;
      }
    });
    return addedLeaves;
  };

  walk(navTree, 0, [], []);
  return { flatGroups, tocEntries };
};

const CatalogueViewer = () => {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { loginViewer, catalogue, pageError } = useCatalogueDatabase();
  const { schemas, allSeasons } = useArticleReader();
  const {
    toggleArticleZoomModal,
    isZoomModalShowing,
    onZoomArticle,
    zoomArticle,
    zoomArticleImages
  } = useArticleZoomModal();
  const {
    isInventoryModalShowing,
    toggleInventoryModal,
    inventoryArticle,
    onViewInventory
  } = useInventoryModal();

  const [viewerLoggedIn, setViewerLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);

  // Sidebar nav data
  const [navTreeBySeason, setNavTreeBySeason] = useState({});
  const [articlesByKey, setArticlesByKey] = useState({});
  const [seasonEntries, setSeasonEntries] = useState([]);
  const [activeSeasonKey, setActiveSeasonKey] = useState(null);

  // Active group for scroll sync
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const virtuosoRef = useRef(null);
  const isProgrammaticScroll = useRef(false);

  // Mobile sidebar drawer
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const currentParams = Object.fromEntries([...searchParams]);
    if (!viewerLoggedIn) {
      loginViewer(currentParams.ctgid).then(() => {
        setViewerLoggedIn(true);
      });
    }
  }, [searchParams, loginViewer, viewerLoggedIn]);

  // Build navigation tree and article groupings from catalogue data
  useEffect(() => {
    if (
      Object.keys(schemas).length === 0 ||
      catalogue == null ||
      catalogue.articles == null
    )
      return;

    const groupingMode = catalogue.groupingMode || "schema";

    if (groupingMode === "custom" && catalogue.customSections) {
      // Custom sections mode: use customSections as the nav tree
      const artMap = {};
      const buildCustomNavTree = (sections) => {
        return sections.map((section) => {
          if (section.groups && section.groups.length > 0) {
            return {
              key: section.id,
              name: section.name,
              name_fr: section.name_fr || section.name,
              groups: buildCustomNavTree(section.groups)
            };
          } else {
            // Leaf node — map articles
            const sectionArticles = catalogue.articles.filter((a) =>
              section.articleIDs?.includes(a.id)
            );
            // Apply per-group sort mode
            const sorted = sortViewerArticles(sectionArticles, section.sortMode, section.articleIDs);
            artMap[section.id] = sorted;
            return {
              key: section.id,
              name: section.name,
              name_fr: section.name_fr || section.name
            };
          }
        });
      };
      const tree = buildCustomNavTree(catalogue.customSections);
      setNavTreeBySeason({ _custom: tree });
      setArticlesByKey(artMap);
      setSeasonEntries([]);
      setActiveSeasonKey("_custom");
    } else {
      // Schema mode (default, backwards compatible)
      const representedSeasons = new Set();
      if (catalogue.seasonMap && catalogue.seasonMap.size > 0) {
        catalogue.seasonMap.forEach((seasonKey) => representedSeasons.add(seasonKey));
      } else {
        catalogue.articles.forEach((article) => {
          article.seasons?.forEach((s) => representedSeasons.add(s));
        });
      }
      if (representedSeasons.size === 0) {
        representedSeasons.add(catalogue.seasonKey);
      }

      const artMap = {};
      const treeMap = {};
      const seasons = [];
      const seasonMap = catalogue.seasonMap;

      representedSeasons.forEach((seasonKey) => {
        if (!schemas[seasonKey]) return;
        const seasonInfo = allSeasons?.find((s) => s.key === seasonKey) || {
          name: catalogue.seasonName || seasonKey,
          name_fr: catalogue.seasonName_fr || seasonKey
        };

        const seasonArticles = catalogue.articles.filter((a) => {
          if (seasonMap && seasonMap.size > 0) {
            return seasonMap.get(a.id) === seasonKey;
          }
          return a.seasons?.includes(seasonKey) || catalogue.seasonKey === seasonKey;
        });

        let existingKeys = [];
        if (catalogue.sortOrder === "collection") {
          existingKeys = seasonArticles
            .map((article) =>
              (article[`${seasonKey}_collectionKeys`] || []).map((key) => key)
            )
            .reduce((flatten, arr) => [...flatten, ...arr], []);
        } else if (catalogue.sortOrder === "category") {
          existingKeys = seasonArticles.map(
            (article) => article[`${seasonKey}_generalCategoryKey`]
          );
        }

        let schemaSource;
        if (catalogue.sortOrder === "collection") {
          schemaSource = cloneDeep(schemas[seasonKey]?.schemas || []);
        } else {
          schemaSource = cloneDeep(schemas[seasonKey]?.categorySchemas || []);
        }

        const filterSchema = (items) => {
          return items
            .filter((item) =>
              existingKeys.some((key) => key?.includes(item.key))
            )
            .map((item) => {
              if (item.groups) {
                item.groups = filterSchema(item.groups);
              }
              if (!item.groups || item.groups.length === 0) {
                let leafArticles;
                if (catalogue.sortOrder === "collection") {
                  leafArticles = seasonArticles.filter((a) =>
                    a[`${seasonKey}_collectionKeys`]?.includes(item.key)
                  );
                  leafArticles.sort((a, b) => {
                    const aSort = a[`${seasonKey}_${item.key}_sort`] || 0;
                    const bSort = b[`${seasonKey}_${item.key}_sort`] || 0;
                    return aSort - bSort;
                  });
                } else {
                  leafArticles = seasonArticles.filter((a) =>
                    a[`${seasonKey}_generalCategoryKey`]?.includes(item.key)
                  );
                  leafArticles.sort((a, b) =>
                    a.retailPrice < b.retailPrice
                      ? 1
                      : a.retailPrice > b.retailPrice
                      ? -1
                      : a.articleName?.toLowerCase() >
                        b.articleName?.toLowerCase()
                      ? 1
                      : -1
                  );
                }
                // Namespace key with season to avoid collisions across seasons
                const leafKey = `${seasonKey}_${item.key}`;
                artMap[leafKey] = leafArticles;
                item.key = leafKey;
                delete item.groups;
              }
              return item;
            });
        };

        const filteredSchema = filterSchema(schemaSource);

        if (filteredSchema.length > 0) {
          treeMap[seasonKey] = filteredSchema;
          seasons.push({
            key: seasonKey,
            name: seasonInfo.name,
            name_fr: seasonInfo.name_fr
          });
        }
      });

      // Sort newest-first using allSeasons order (chronological asc from Firestore)
      const seasonOrder = (allSeasons || []).map((s) => s.key);
      seasons.sort((a, b) => seasonOrder.indexOf(b.key) - seasonOrder.indexOf(a.key));

      setNavTreeBySeason(treeMap);
      setArticlesByKey(artMap);
      setSeasonEntries(seasons);
      setActiveSeasonKey((prev) => {
        // Keep current season if still valid, otherwise pick newest
        if (prev && treeMap[prev]) return prev;
        return seasons[0]?.key || null;
      });
    }
  }, [catalogue, schemas, allSeasons, i18n.language]);

  // Derive flat structures for Virtuoso + TOC from the active season's tree
  const activeNavTree = navTreeBySeason[activeSeasonKey] || [];
  const { flatGroups, tocEntries } = useMemo(
    () => flattenNavTree(activeNavTree, articlesByKey),
    [activeNavTree, articlesByKey]
  );

  // Season picker → switch active season, reset scroll
  const onSeasonChange = useCallback((seasonKey) => {
    setActiveSeasonKey(seasonKey);
    setActiveGroupIndex(0);
    virtuosoRef.current?.scrollToIndex({ index: 0, align: "start" });
  }, []);

  // TOC click → scroll Virtuoso to group (and close mobile sidebar)
  const onSelectGroup = useCallback((groupIndex) => {
    setActiveGroupIndex(groupIndex);
    setIsSidebarOpen(false);
    isProgrammaticScroll.current = true;
    virtuosoRef.current?.scrollToIndex({
      index: groupIndex,
      align: "start"
    });
    setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 100);
  }, []);

  // Virtuoso scroll → update active group in TOC
  const onRangeChanged = useCallback(({ startIndex }) => {
    if (!isProgrammaticScroll.current) {
      setActiveGroupIndex(startIndex);
    }
  }, []);

  return (
    <BookingPublicLayout
      title={catalogue?.name}
      showMenuButton
      onMenuToggle={() => setIsSidebarOpen((prev) => !prev)}
    >
      <Wrapper>
        <Loading isLoading={loading} />
        {pageError ? (
          <PageError>This Catalogue is no longer available</PageError>
        ) : (
          <ViewerLayout>
            <Backdrop
              $isOpen={isSidebarOpen}
              onClick={() => setIsSidebarOpen(false)}
            />
            <SidebarContainer $isOpen={isSidebarOpen}>
              {(tocEntries.length > 0 || seasonEntries.length > 0) && (
                <CatalogueViewerTOC
                  tocEntries={tocEntries}
                  activeGroupIndex={activeGroupIndex}
                  onSelectGroup={onSelectGroup}
                  seasonEntries={seasonEntries}
                  activeSeasonKey={activeSeasonKey}
                  onSeasonChange={onSeasonChange}
                />
              )}
            </SidebarContainer>
            <ContentContainer>
              {flatGroups.length > 0 && (
                <GroupedVirtuoso
                  ref={virtuosoRef}
                  style={{ height: "100%" }}
                  groupCounts={flatGroups.map(() => 1)}
                  rangeChanged={onRangeChanged}
                  groupContent={(index) => {
                    const group = flatGroups[index];
                    const crumbs =
                      i18n.language === "en"
                        ? group.breadcrumb
                        : group.breadcrumb_fr;
                    const parentCrumbs = crumbs.slice(0, -1);
                    const leafCrumb = crumbs[crumbs.length - 1];
                    return (
                      <SectionHeader>
                        <HeaderBreadCrumbs>
                          {parentCrumbs.length > 0 && (
                            <BreadcrumbPath>
                              {parentCrumbs.map((segment, i) => (
                                <Fragment key={i}>
                                  {i > 0 && (
                                    <BreadcrumbSeparator>
                                      &#8227;
                                    </BreadcrumbSeparator>
                                  )}
                                  <span>{segment}</span>
                                </Fragment>
                              ))}
                            </BreadcrumbPath>
                          )}
                          <BreadcrumbLeaf>{leafCrumb}</BreadcrumbLeaf>
                        </HeaderBreadCrumbs>
                      </SectionHeader>
                    );
                  }}
                  itemContent={(index) => {
                    const group = flatGroups[index];
                    return (
                      <SectionGrid>
                        {group.articles.map((article) => (
                          <ArticleCard
                            key={article.id}
                            article={article}
                            seasonKey={activeSeasonKey === "_custom" ? catalogue?.seasonKey : activeSeasonKey}
                            showInventoryButton
                            shouldShowLimitedSoldOut
                            priceToShow={catalogue?.priceToShowPreview}
                            onZoomArticle={onZoomArticle}
                            onViewInventory={onViewInventory}
                          />
                        ))}
                      </SectionGrid>
                    );
                  }}
                />
              )}
            </ContentContainer>
          </ViewerLayout>
        )}
      </Wrapper>
      <ArticleZoomModal
        show={isZoomModalShowing}
        onClose={toggleArticleZoomModal}
        imageList={zoomArticleImages}
        article={zoomArticle}
      />
      <InventoryModal
        show={isInventoryModalShowing}
        onClose={toggleInventoryModal}
        article={inventoryArticle}
      />
    </BookingPublicLayout>
  );
};

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const ViewerLayout = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Backdrop = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 99;
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
    transition: opacity 250ms ease;
  }
`;

const SidebarContainer = styled.div`
  flex-shrink: 0;
  width: 280px;
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  border-right: 1px solid ${COLORS.secondary};
  background: white;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 100;
    transform: translateX(${({ $isOpen }) => ($isOpen ? "0" : "-100%")});
    transition: transform 250ms ease;
    box-shadow: ${({ $isOpen }) =>
      $isOpen ? "2px 0 8px rgba(0,0,0,0.15)" : "none"};
  }
`;

const ContentContainer = styled.div`
  flex-grow: 1;
  height: 100%;
  overflow: hidden;
  background: ${COLORS.tertiary};
`;

const SectionHeader = styled.div`
  z-index: 15;
  width: 100%;
  padding: 10px 5px;
  background-color: ${COLORS.secondary};
  border-bottom: 1px solid ${COLORS.secondary};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: 8px 5px;
  }
`;

const HeaderBreadCrumbs = styled.div`
  color: ${COLORS.text};
  text-transform: uppercase;
  font-weight: ${FONTWEIGHT.heavy};
  padding-left: 10px;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding-left: 5px;
  }
`;

const BreadcrumbPath = styled.div`
  display: flex;
  align-items: center;
  font-size: ${FONTSIZE.small};

  span {
    padding: 3px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block;
    font-size: 11px;
    font-weight: ${FONTWEIGHT.medium};
    color: ${COLORS.text};
  }
`;

const BreadcrumbLeaf = styled.div`
  color: ${COLORS.primary};
  font-size: ${FONTSIZE.medium};
  padding: 3px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONTSIZE.small};
  }
`;

const BreadcrumbSeparator = styled.span`
  color: ${COLORS.text};
  font-size: ${FONTSIZE.xsmall};
  padding: 0 2px;
`;

const SectionGrid = styled.div`
  padding: 5px;
  padding-bottom: 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 0.33333fr));
  gap: 5px;
  align-content: start;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const PageError = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.large};
`;

export default CatalogueViewer;
